import { memo, ReactNode, useMemo } from 'react';
import { Input, InputProps, SearchField, SearchFieldProps } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Label, LabelProps } from '@/components/core';
import { CloseIcon, SearchIcon } from '@/components/icons';
import { generateId } from '@/utils';

export type SearchProps = {
  autocompleteOptions?: string[];
  children?: ReactNode;
  clear?: () => void;
  props?: {
    input?: InputProps;
    label?: LabelProps;
    searchField?: SearchFieldProps;
  };
};

const ISearch = ({ autocompleteOptions, children, clear, props }: SearchProps) => {
  const autocompleteId = useMemo(() => {
    return generateId();
  }, []);

  const { className: inputClassName, ...inputProps } = props?.input ?? {};
  const { className: labelClassName, ...labelProps } = props?.label ?? {};
  const { className: searchFieldClassName, ...searchFieldProps } = props?.searchField ?? {};

  return (
    <SearchField
      className={merge(
        'focus-within:ring-primary-400 gap-sm px-sm py-xs flex flex-row items-center rounded-md border-1 border-gray-200 bg-white text-sm focus-within:ring-1',
        searchFieldClassName
      )}
      {...searchFieldProps}
    >
      <Label className={labelClassName} {...labelProps}>
        <SearchIcon />
      </Label>
      <Input
        list={autocompleteId}
        className={merge(
          'outline-none placeholder:text-gray-500 [&::-webkit-search-cancel-button]:hidden',
          inputClassName
        )}
        {...inputProps}
      >
        {children}
      </Input>
      {clear && (!inputProps || inputProps.value) ? (
        <>
          <div className="grow" />
          <Button className="!p-0" onPress={clear} variant="plain">
            <CloseIcon />
          </Button>
        </>
      ) : null}
      <datalist id={autocompleteId}>
        {autocompleteOptions?.map((a, i) => (
          <option key={i} value={a} />
        ))}
      </datalist>
    </SearchField>
  );
};

export const Search = memo(ISearch) as typeof ISearch;
